import { Component } from '@theme/component';

/**
 * @typedef {Object} PtaCollectionFinderRefs
 * @property {HTMLSelectElement} other - The platform on the far side of the pair
 * @property {HTMLElement} lead - Sentence describing the current direction
 * @property {HTMLButtonElement} [flip] - Only present when the platform can be either side
 */

const BATTERY_FIRST = 'battery';
const TOOL_FIRST = 'tool';

/**
 * The finder with one side already pinned to the collection's platform.
 *
 * Shares `snippets/pta-finder-data.liquid` with the main finder, so there is one matrix in
 * the theme and no second thing to keep in step.
 *
 * Reads `?dir=battery|tool` to pick which side the collection's platform sits on, and
 * `?to=` / `?from=` to preselect the other half — which is what lets a menu link land on a
 * directional view of a collection that is otherwise identical in both panels.
 *
 * @extends {Component<PtaCollectionFinderRefs>}
 */
class PtaCollectionFinder extends Component {
  /** @type {{platforms: string[], platformHandles: string[], pairs: [number, number, string][]}} */
  #data = { platforms: [], platformHandles: [], pairs: [] };

  #forward = new Map();
  #reverse = new Map();
  #direction = BATTERY_FIRST;
  #pinned = -1;

  /**
   * The matrix as an ES module from `assets/`, picked by locale in Liquid, rather than a `<script>` island
   * inlined into every brand collection page. Fetched once, then served from cache.
   *
   * Asynchronous, so the select is disabled and labelled with a translated "loading" string until it
   * lands — disabled rather than empty, because a select a shopper can open and find empty reads as
   * broken.
   */
  async #load() {
    const src = this.dataset.src;
    if (!src) return false;

    const other = this.refs.other;
    if (other) {
      other.disabled = true;
      other.replaceChildren(new Option(this.dataset.loading ?? '', ''));
    }

    try {
      const mod = await import(src);
      this.#data = mod.default ?? mod;
      return Array.isArray(this.#data?.pairs);
    } catch {
      return false;
    }
  }

  async connectedCallback() {
    super.connectedCallback();

    /* Hidden rather than left alone on a failed load. `#load` disables the select and labels it
     * with the "loading" string *before* it fetches, so returning early here left a dead control
     * reading "Loading adapters…" in perpetuity — the same visible end state as the `#pinned < 0`
     * branch below, which was already meant to hide. A finder that cannot be filled is not a
     * finder, and an inert card is worse than no card. */
    if (!(await this.#load())) {
      this.hidden = true;
      return;
    }

    /* Matched on the platform's **handle**, never its name.
     *
     * This read `platforms.indexOf(this.dataset.platform)` against the platform's translated name. The
     * names in the data were English, so on /fr the collection's "Bosch 18V GBA (Bleu)" matched nothing,
     * `#pinned` came back -1, and the block hid itself — a brand collection page with an empty dropdown in
     * French while English worked. Handles are language-neutral, so this cannot drift again. */
    this.#pinned = (this.#data.platformHandles ?? []).indexOf(this.dataset.platformHandle ?? '');

    // A platform the matrix has never seen has nothing to offer — leave the block inert
    // rather than showing an empty control.
    if (this.#pinned < 0) {
      this.hidden = true;
      return;
    }

    for (const [battery, tool, handle] of this.#data.pairs) {
      if (!this.#forward.has(battery)) this.#forward.set(battery, new Map());
      if (!this.#reverse.has(tool)) this.#reverse.set(tool, new Map());

      this.#forward.get(battery)?.set(tool, handle);
      this.#reverse.get(tool)?.set(battery, handle);
    }

    /* A platform can be *in* the matrix and still pair with nothing on the side this collection
     * shows — `?dir=` overrides `version2.side`, so a link can ask for the direction a platform has
     * no adapters in. `#render` would then fill the select with its placeholder and nothing else,
     * which reads as broken rather than as empty. Checked across both maps rather than the current
     * direction alone, since the flip button can reach the other one. */
    if (!this.#forward.get(this.#pinned)?.size && !this.#reverse.get(this.#pinned)?.size) {
      this.hidden = true;
      return;
    }

    if (this.refs.other) this.refs.other.disabled = false;

    this.#direction = this.#initialDirection();

    this.refs.flip?.addEventListener('click', () => {
      this.#direction = this.#direction === BATTERY_FIRST ? TOOL_FIRST : BATTERY_FIRST;
      this.#render();
      this.refs.other?.focus();
    });

    this.refs.other?.addEventListener('change', () => this.#go());

    this.#render();
    this.#preselect();
  }

  /**
   * `?dir=` wins; otherwise the collection's own side decides, since a battery-only platform
   * cannot be the tool half of a pair and vice versa.
   */
  #initialDirection() {
    const wanted = new URLSearchParams(window.location.search).get('dir');

    if (wanted === BATTERY_FIRST || wanted === TOOL_FIRST) return wanted;
    if (this.dataset.side === TOOL_FIRST) return TOOL_FIRST;

    return BATTERY_FIRST;
  }

  /** Platforms that pair with the pinned one, in the current direction. */
  get #matches() {
    const lookup = this.#direction === BATTERY_FIRST ? this.#forward : this.#reverse;

    return lookup.get(this.#pinned) ?? new Map();
  }

  #render() {
    const { other, lead } = this.refs;
    const batteryFirst = this.#direction === BATTERY_FIRST;

    this.dataset.direction = this.#direction;

    if (lead) lead.textContent = this.dataset[batteryFirst ? 'runsTools' : 'runBy'] ?? '';

    if (!other) return;

    const placeholder = this.dataset[batteryFirst ? 'selectTool' : 'selectBattery'] ?? '';
    /* DOM nodes rather than an `innerHTML` string — `new Option(text, value)` assigns the label as
       text, so a platform name is never parsed as markup. Same change and same reasoning as
       `pta-finder.js`'s `#fill`, which carries the full note. */
    const sorted = [...this.#matches.keys()]
      .map((index) => ({ index, name: this.#data.platforms[index] }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

    other.replaceChildren(
      new Option(placeholder, ''),
      ...sorted.map(({ index, name }) => new Option(name, index))
    );
    other.setAttribute('aria-label', placeholder);
  }

  #go() {
    const handle = this.#matches.get(Number(this.refs.other?.value));

    if (!handle) return;

    const root = this.dataset.rootUrl === '/' ? '' : (this.dataset.rootUrl ?? '');

    window.location.href = `${root}/products/${handle}`;
  }

  /** `?to=` / `?from=` name the far platform; either works, whichever reads better in a link. */
  #preselect() {
    const params = new URLSearchParams(window.location.search);
    const wanted = params.get('to') ?? params.get('from');

    if (!wanted || !this.refs.other) return;

    const index = this.#data.platforms.indexOf(wanted);
    const match = [...this.refs.other.options].find((option) => option.value === String(index));

    if (match) this.refs.other.value = String(index);
  }
}

customElements.define('pta-collection-finder', PtaCollectionFinder);
